import { FC } from 'react'
import { FormProvider } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { FormGenerator, ModalWrapper } from '@microservices/wiskey-react-components'
import { Box, CircularProgress, Grid } from '@mui/material'

import { CommandPickerController } from '@components/hookFormControllers/CommandPickerController'
import { ScriptValueDialog } from '@components/ScriptValueDialog'

import { checkValidJS, getScriptValidationContext } from '@helpers'
import { MODAL_TYPE } from '@constants'
import { ActionRow, GETAction, ModalType, Table } from '@types'

import { useAddActionDialog } from './hooks'

type AddActionDialog = {
  outerParametersId: number
  entityId: number
  currentAction: ActionRow | null
  actionTable: Table<GETAction>
  showDialog: boolean
  modalType: ModalType
  onCloseModal: (isDirty?: boolean) => void
  onUpdateActions: (data: ActionRow, modalType: ModalType) => void
}

export const AddActionDialog: FC<AddActionDialog> = ({
  outerParametersId,
  entityId,
  currentAction,
  actionTable,
  showDialog,
  modalType,
  onCloseModal,
  onUpdateActions,
}) => {
  const { t } = useTranslation()
  const {
    objectCode,
    isDirty,
    actionInputs,
    methods,
    isLoading,
    defaultValues,
    handleSave,
    reset,
    isOpenScriptValueDialog,
    setIsOpenScriptValueDialog,
    currentNameScriptDialog,
  } = useAddActionDialog({
    outerParametersId,
    entityId,
    currentRow: currentAction,
    actionTable,
    modalType,
    onCloseModal,
    onUpdateActions,
  })

  const { watch, setValue } = methods

  const watchJsValue = watch('jsValue')

  const handleSaveScriptValueDialog = (value: string) => {
    if (!currentNameScriptDialog) return

    setValue(currentNameScriptDialog, value, { shouldDirty: true })
  }

  const handleCloseScriptValueDialog = () => {
    setIsOpenScriptValueDialog(false)
  }

  const handleCloseModal = () => {
    reset(defaultValues)
    onCloseModal(isDirty)
  }

  return (
    <ModalWrapper
      disabledSave={!isDirty}
      isShow={showDialog}
      title={`${t(`addDialog.${modalType}`)} Action`}
      width={1020}
      containerStyle={{
        px: 0.5,
        py: 0.5,
        // borderRadius: 0
      }}
      onClose={handleCloseModal}
      onSave={handleSave}
    >
      {isLoading && modalType === MODAL_TYPE.EDIT ? (
        <Grid container alignItems={'center'} justifyContent={'center'} my={3}>
          <CircularProgress />
        </Grid>
      ) : (
        <Box maxHeight={600} overflow='auto' pr={1} pt={1}>
          <FormProvider {...methods}>
            <form>
              <FormGenerator inputs={actionInputs} mainGridSpacing={0} />
              <CommandPickerController
                canChangeRefObject
                isAnotherObjectWithRequestData
                isSingle
                isTypeHidden
                commandName='name'
                name='command'
                objectCode={objectCode}
              />
            </form>
            {isOpenScriptValueDialog && (
              <ScriptValueDialog
                windowBoundaries
                isShow={isOpenScriptValueDialog}
                modalType={'edit'}
                objectFields={[]}
                value={watchJsValue}
                validator={value =>
                  checkValidJS(
                    value,
                    getScriptValidationContext({
                      srcObj: { variables: {} },
                    })
                  )
                }
                onClose={handleCloseScriptValueDialog}
                onSave={handleSaveScriptValueDialog}
              />
            )}
          </FormProvider>
        </Box>
      )}
    </ModalWrapper>
  )
}
